import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { Box, Button, ButtonBase, CircularProgress, Dialog, IconButton, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import { getAttachementById } from "../../ReduxTLK/features/Attachement/AttachementSlice";
import { attachmentKind } from "./chatTimeline";

const FONT = '"DM Sans", sans-serif';

const KIND_META = {
  image: { label: 'Image', action: 'Aperçu', Icon: ImageRoundedIcon, color: '#6941C6', bg: '#EFEBFE' },
  pdf: { label: 'PDF', action: 'Aperçu', Icon: PictureAsPdfRoundedIcon, color: '#D92D20', bg: '#FEE4E2' },
  document: { label: 'Document Word', action: 'Télécharger', Icon: DescriptionRoundedIcon, color: '#1671D9', bg: '#E8F1FC' },
  sheet: { label: 'Tableur Excel', action: 'Télécharger', Icon: TableChartRoundedIcon, color: '#067647', bg: '#DCFAE6' },
  audio: { label: 'Message vocal', action: 'Écouter', Icon: GraphicEqRoundedIcon, color: '#C4320A', bg: '#FEF0E6' },
  file: { label: 'Fichier', action: 'Télécharger', Icon: InsertDriveFileRoundedIcon, color: '#475467', bg: '#EEF0F3' },
};

// Attachments are fetched on demand (base64) and exposed as a blob URL, which works for large PDFs too
async function loadAttachment(dispatch, attachment) {
  const res = await dispatch(getAttachementById(attachment.id)).then(unwrapResult);
  const result = res?.data?.data?.result;
  if (!result?.contenu) throw new Error('Pièce jointe vide');
  const type = result.type || attachment.type || 'application/octet-stream';
  const bytes = Uint8Array.from(atob(result.contenu), (char) => char.charCodeAt(0));
  return {
    url: URL.createObjectURL(new Blob([bytes], { type })),
    name: result.nom || attachment.nom || 'fichier',
  };
}

function saveFile({ url, name }) {
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function AttachmentCard({ attachment, isOwn, onPreview }) {
  const dispatch = useDispatch();
  const kind = attachmentKind(attachment);
  const meta = KIND_META[kind];
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);

  useEffect(() => () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
  }, [audioUrl]);

  const handleClick = async () => {
    if (kind === 'image' || kind === 'pdf') {
      onPreview(attachment);
      return;
    }
    setBusy(true);
    setFailed(false);
    try {
      const file = await loadAttachment(dispatch, attachment);
      if (kind === 'audio') {
        setAudioUrl(file.url);
      } else {
        saveFile(file);
        setTimeout(() => URL.revokeObjectURL(file.url), 1000);
      }
    } catch (err) {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        width: 280,
        maxWidth: '85%',
        borderRadius: '12px',
        border: '1px solid',
        borderColor: isOwn ? '#C9DEF8' : '#EAECF0',
        bgcolor: isOwn ? '#E3EFFD' : '#FFFFFF',
        overflow: 'hidden',
      }}
    >
      <ButtonBase
        onClick={handleClick}
        disabled={busy || Boolean(audioUrl)}
        sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', gap: 1.25, p: 1.25, textAlign: 'left' }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: '10px',
            display: 'grid',
            placeItems: 'center',
            bgcolor: meta.bg,
            color: meta.color,
          }}
        >
          {busy ? <CircularProgress size={18} color="inherit" /> : <meta.Icon fontSize="small" />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap title={attachment.nom} sx={{ fontFamily: FONT, fontSize: '13.5px', fontWeight: 600, color: '#101828' }}>
            {attachment.nom || meta.label}
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: failed ? '#D92D20' : '#667085' }}>
            {failed ? 'Échec du chargement — cliquer pour réessayer' : `${meta.label} · ${meta.action}`}
          </Typography>
        </Box>
      </ButtonBase>
      {audioUrl && (
        <Box sx={{ px: 1.25, pb: 1.25 }}>
          <audio controls autoPlay src={audioUrl} style={{ width: '100%', height: 36 }}>
            Votre navigateur ne supporte pas l'élément audio
          </audio>
        </Box>
      )}
    </Box>
  );
}

export function AttachmentPreviewDialog({ attachment, open, onClose }) {
  const dispatch = useDispatch();
  const kind = attachmentKind(attachment);
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    if (!attachment) return undefined;
    let cancelled = false;
    let loadedUrl = null;
    setFile(null);
    setStatus('loading');
    loadAttachment(dispatch, attachment)
      .then((loaded) => {
        loadedUrl = loaded.url;
        if (cancelled) {
          URL.revokeObjectURL(loaded.url);
          return;
        }
        setFile(loaded);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
      if (loadedUrl) URL.revokeObjectURL(loadedUrl);
    };
  }, [attachment, dispatch]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: kind === 'pdf' ? 'min(1000px, 92vw)' : 'auto',
          maxWidth: '92vw',
          borderRadius: '12px',
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: '1px solid #EAECF0' }}>
        <Typography noWrap sx={{ flex: 1, minWidth: 0, fontFamily: FONT, fontSize: '14px', fontWeight: 600, color: '#101828' }}>
          {attachment?.nom}
        </Typography>
        <Button
          size="small"
          startIcon={<DownloadRoundedIcon />}
          disabled={!file}
          onClick={() => saveFile(file)}
          sx={{ textTransform: 'none', fontFamily: FONT, fontWeight: 600 }}
        >
          Télécharger
        </Button>
        <IconButton size="small" onClick={onClose} aria-label="Fermer">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box
        sx={{
          minWidth: 320,
          minHeight: 240,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#F2F4F7',
          p: kind === 'pdf' ? 0 : 2,
        }}
      >
        {status === 'loading' && <CircularProgress size={28} />}
        {status === 'error' && (
          <Typography sx={{ fontFamily: FONT, fontSize: '14px', color: '#475467' }}>
            Un problème est survenu lors du chargement du fichier.
          </Typography>
        )}
        {status === 'ready' && kind === 'image' && (
          <img
            src={file.url}
            alt={attachment?.nom}
            style={{ display: 'block', maxWidth: '100%', maxHeight: '78vh', objectFit: 'contain', borderRadius: 8 }}
          />
        )}
        {status === 'ready' && kind === 'pdf' && (
          <iframe src={file.url} title={attachment?.nom} style={{ width: '100%', height: '80vh', border: 0 }} />
        )}
      </Box>
    </Dialog>
  );
}
